import React, { useState } from "react";
import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import MainLayout from "../../components/layout/MainLayout";
import { ProductItem } from "../../components/common/ProductItem.comp";
import { Product, ProductFilterFormData } from "../../types/product";
import { Pagination } from "../../components/pagination.comp";
import { ItemsPerPageSelector } from "../../components/itemsPerPage.comp";
import productCore from "../../core/product.core";
import { LoadingComp } from "../../components/common/loading.comp";
import ArticleBanner from "../../components/articleBanner.comp";
import { useTranslation } from "../../core/translation.core";

export default function NoWastePage() {
  return (
    <MainLayout title="唔准嘥嘢專區 | No-Waste Products" url="/article/no-waste">
      <Main />
    </MainLayout>
  );
}

function Main() {
  const { t, msg } = useTranslation();
  const router = useRouter();
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [wishlistedItems, setWishlistedItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(18);

  // Fetch products using React Query
  const {
    data: productsData,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ["productListByCatSlug", "no-waste", currentPage, itemsPerPage, router.locale],
    queryFn: () =>
      productCore.filter(
        {
          catSlug: "唔准嘥嘢",
          page: currentPage,
          size: itemsPerPage,
        } as ProductFilterFormData,
        router.locale
      ),
    enabled: router.isReady,
  });

  // Clear success message after 3 seconds
  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Function to update wishlist items
  const updateWishlistItem = (productName: string, wishlistStatus: boolean) => {
    const newWishlisted = new Set(wishlistedItems);
    if (wishlistStatus) {
      newWishlisted.add(productName);
      setSuccessMessage(`已加入"${productName}"到願望清單`);
    } else {
      newWishlisted.delete(productName);
      setSuccessMessage(`已從願望清單移除"${productName}"`);
    }
    setWishlistedItems(newWishlisted);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Get products and total from query data
  const products = productsData?.list || [];
  const totalItems = productsData?.size ?? 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Calculate pagination display info
  const startIdx = (currentPage - 1) * itemsPerPage;
  const endItem = Math.min(startIdx + itemsPerPage, totalItems);
  const startItem = totalItems > 0 ? startIdx + 1 : 0;

  return (
    <div className="min-h-screen bg-guru-floral-white">
      {/* Custom Banner Section */}
      <ArticleBanner title="唔准嘥嘢專區" />

      {/* Description Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-8">
          <p className="text-sm md:text-base mb-4">
            每日都會有好多「過期」、「即將過期」或「包裝損壞」，但產品本身仍然可以使用既貨品，被大量送到堆填區。
          </p>
          <p className="text-sm md:text-base mb-4">
            顧隨顧師作為一間零售商，同時亦係批發商，十分明白當中既無奈。但傳統超級市場或零售店，並無積極面對嘥嘢問題，在往只會選比供應商，甚至直接當垃圾賣。
          </p>
          <p className="text-sm md:text-base mb-4">
            打雞一直認為，作為一間本土公司，除咗服務更、香港人，同時亦應該為社會、為環境負責任及作出貢獻，希望藉住呢個專區減少被棄置既物資。
          </p>
          <p className="text-sm md:text-base mb-4">大家記住「唔准嘥嘢」！！</p>
          <p className="text-sm md:text-base font-bold flex items-center">
            <span className="text-yellow-600 mr-2">⚠️</span>
            唔准嘥嘢可以連同其他貨品一齊埋單，但不會計入免運費，亦不設任何會員優惠及回贈，只會計算入累積消費
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="fixed top-20 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50">
            {successMessage}
          </div>
        )}

        {/* Products Section */}
        {/* <h2 className="text-xl md:text-2xl font-semibold mb-6">No-Waste Products</h2> */}

        {isPending ? (
          <LoadingComp padding="py-8" />
        ) : isError ? (
          <div className="text-center py-4 text-red-500">Error: {error.message}</div>
        ) : products && products.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 px-2">
              {products.map((product: Product) => (
                <div className="flex justify-center" key={product.id}>
                  <ProductItem
                    key={product.id}
                    product={product}
                    wishList={wishlistedItems.has(product.id)}
                    updateItem={updateWishlistItem}
                    showDiscount={true}
                  />
                </div>
              ))}
            </div>

            {/* Pagination and Items Per Page Section */}
            <div className="mt-10 mb-6">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div className="mb-4 md:mb-0">
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                </div>

                <div className="hidden md:block">
                  <ItemsPerPageSelector
                    itemsPerPage={itemsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    options={[12, 18, 24]}
                    totalItems={totalItems}
                    startItem={startItem}
                    endItem={endItem}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4 text-gray-500">{t(msg.product.noProductsAvailable)}</div>
        )}
      </div>
    </div>
  );
}
